import { useForm } from "react-hook-form";
import React, { FC, useEffect, useState } from "react";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputLabel,
	TextField,
	Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import {
	useCreateClientMutation,
	useCreateNodeMutation,
	useEditClientMutation,
	useEditNodeMutation
} from "../../../redux/reducers/settings.reducer";
import NumberInput from "../../../common/NumberInput";

export interface IClientNodeDetails {
	clientId: string;
	clientName: string;
	nodeId: string;
	nodeUsername: string;
}

interface IClientNodeDetailsDialogProps {
	open: boolean;
	clientNodeDetails: IClientNodeDetails;
	createdClientId: number;
	createdNodeId: number;
	onClose: () => void;
}

const ClientNodeDetailsDialog: FC<IClientNodeDetailsDialogProps> = (props) => {
	const { open, clientNodeDetails, createdClientId, createdNodeId, onClose } = props;

	const [saveButtonLoading, setSaveButtonLoading] = useState<boolean>(false);
	const [apiError, setApiError] = useState<string>("");
	const [createdIds, setCreatedIds] = useState<{ client: number; node: number }>({ client: -1, node: -1 });

	// APIS
	// CREATE CLIENT
	const [createClient] = useCreateClientMutation();

	// EDIT CLIENT
	const [editClient] = useEditClientMutation();

	// CREATE NODE
	const [createNode] = useCreateNodeMutation();

	// EDIT NODE
	const [editNode] = useEditNodeMutation();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IClientNodeDetails>();

	function createClientDetails(clientId: string, clientName: string): Promise<number> {
		return new Promise((resolve, reject) => {
			createClient({
				client_id: Number(clientId),
				name: clientName,
				point_of_payment: 0,
				city: "NA",
				area: "NA",
				pincode: "0"
			})
				.unwrap()
				.then((response) => {
					setCreatedIds((prevState) => ({ ...prevState, client: response.id }));
					resolve(response.id);
				})
				.catch(reject);
		});
	}

	function createNodeDetails(nodeId: string, nodeName: string, clientPK: number): Promise<void> {
		return new Promise((resolve, reject) => {
			createNode({
				node_id: Number(nodeId),
				device_id: nodeId,
				username: nodeName,
				local_ip_address: "NA",
				global_ip_address: "NA",
				hostname: "NA",
				mac_address: "NA",
				client: clientPK
			})
				.unwrap()
				.then((response) => {
					setCreatedIds((prevState) => ({ ...prevState, node: response.id }));
					resolve();
				})
				.catch(reject);
		});
	}

	function editClientDetails(clientId: string, clientName: string, clientPK: number) {
		return new Promise((resolve, reject) => {
			editClient({
				id: clientPK,
				data: {
					client_id: Number(clientId),
					name: clientName,
					point_of_payment: 0,
					city: "NA",
					area: "NA",
					pincode: "0"
				}
			})
				.unwrap()
				.then(resolve)
				.catch(reject);
		});
	}

	function editNodeDetails(nodeId: string, nodeName: string, nodePK: number, clientPK: number) {
		return new Promise((resolve, reject) => {
			editNode({
				id: nodePK,
				data: {
					node_id: Number(nodeId),
					device_id: nodeId,
					username: nodeName,
					client: clientPK,
					hostname: "NA",
					local_ip_address: "NA",
					mac_address: "NA"
				}
			})
				.unwrap()
				.then(resolve)
				.catch(reject);
		});
	}

	function handleSubmitForm(formData: IClientNodeDetails) {
		setSaveButtonLoading(true);
		setApiError("");

		if (createdClientId < 0 && createdIds.client < 0) {
			// client is not created
			// create client first then create node

			createClientDetails(formData.clientId, formData.clientName)
				.then((clientPK) => {
					createNodeDetails(formData.nodeId, formData.nodeUsername, clientPK)
						.then(() => {
							setApiError("");
							setSaveButtonLoading(false);
							onClose();
						})
						.catch((error) => {
							setApiError(error.data ? String(error.data) : "Failed to create node");
							setSaveButtonLoading(false);
						});
				})
				.catch((error) => {
					setApiError(error.data ? String(error.data) : "Failed to create client");
					setSaveButtonLoading(false);
				});
		} else {
			// client is already created

			const clientPK = createdClientId >= 0 ? createdClientId : createdIds.client >= 0 ? createdIds.client : 1;

			if (createdNodeId < 0 && createdIds.node < 0) {
				// node is not created
				// edit client & create node simultaneously

				const promiseArray = [
					editClientDetails(formData.clientId, formData.clientName, clientPK),
					createNodeDetails(formData.nodeId, formData.nodeUsername, clientPK)
				];

				Promise.all(promiseArray)
					.then(() => {
						setApiError("");
						setSaveButtonLoading(false);
						onClose();
					})
					.catch((error) => {
						setApiError(error.data ? String(error.data) : "Something went wrong");
						setSaveButtonLoading(false);
					});
			} else {
				// node is already created
				// edit client & edit node simultaneously

				const nodePK = createdNodeId >= 0 ? createdNodeId : createdIds.node >= 0 ? createdIds.node : 1;

				const promiseArray = [
					editClientDetails(formData.clientId, formData.clientName, clientPK),
					editNodeDetails(formData.nodeId, formData.nodeUsername, nodePK, clientPK)
				];

				Promise.all(promiseArray)
					.then(() => {
						setApiError("");
						setSaveButtonLoading(false);
						onClose();
					})
					.catch((error) => {
						setApiError(error.data ? String(error.data) : "Something went wrong");
						setSaveButtonLoading(false);
					});
			}
		}
	}

	useEffect(() => {
		if (open && clientNodeDetails) {
			reset({
				clientId: clientNodeDetails.clientId,
				clientName: clientNodeDetails.clientName,
				nodeId: clientNodeDetails.nodeId,
				nodeUsername: clientNodeDetails.nodeUsername
			});
		} else {
			reset({
				clientId: "",
				clientName: "",
				nodeId: "",
				nodeUsername: ""
			});
		}
	}, [clientNodeDetails, open, reset]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle textAlign="center" color="white" classes={{ root: `dialog-title-root primary` }}>
				Client and Node Details
				<Box className="close-dialog-icon-wrapper">
					<IconButton color="inherit" onClick={onClose}>
						<CloseIcon color="inherit" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "dialog-content-root" }}>
				<Grid container spacing={2} component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="client-id-input"
							error={!!errors.clientId}
							sx={{ color: "#424242", fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Client ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							variant="filled"
							size="small"
							id="client-id-input"
							placeholder="Enter the Client ID"
							autoComplete="off"
							error={!!errors.clientId}
							helperText={errors.clientId && errors.clientId.message ? errors.clientId.message.toString() : ""}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "filled-input-root" } }}
							{...register("clientId", { required: "Client ID is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="client-name-input"
							error={!!errors.clientName}
							sx={{ color: "#424242", fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Client Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							size="small"
							id="client-name-input"
							placeholder="Enter the Client Name"
							autoComplete="off"
							error={!!errors.clientName}
							helperText={errors.clientName && errors.clientName.message ? errors.clientName.message.toString() : ""}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "filled-input-root" } }}
							{...register("clientName", { required: "Client name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="node-id-input"
							error={!!errors.nodeId}
							sx={{ color: "#424242", fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Node ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							variant="filled"
							size="small"
							id="node-id-input"
							placeholder="Enter the Node ID"
							autoComplete="off"
							error={!!errors.nodeId}
							helperText={errors.nodeId && errors.nodeId.message ? errors.nodeId.message.toString() : ""}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "filled-input-root" } }}
							{...register("nodeId", { required: "Node ID is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="node-name-input"
							error={!!errors.nodeUsername}
							sx={{ color: "#424242", fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Node Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							size="small"
							id="node-name-input"
							placeholder="Enter the Node Name"
							autoComplete="off"
							error={!!errors.nodeUsername}
							helperText={
								errors.nodeUsername && errors.nodeUsername.message ? errors.nodeUsername.message.toString() : ""
							}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "filled-input-root" } }}
							{...register("nodeUsername", { required: "Node name is required" })}
						/>
					</Grid>

					{apiError ? (
						<Grid item xs={12}>
							<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
								{apiError}
							</Typography>
						</Grid>
					) : null}

					<Grid item xs={12}>
						<Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
							<LoadingButton
								disableElevation
								variant="contained"
								color="success"
								type="submit"
								startIcon={<SaveIcon />}
								loading={saveButtonLoading}
								loadingPosition="start"
							>
								Save
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default ClientNodeDetailsDialog;
